import { BookMarked, CodeXml, File, FileText, Image, X } from "lucide-react";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { ContextMenu } from "../components/ui";
import type { TreeEntry } from "../lib/api";

const IMAGE = /\.(png|jpe?g|gif|svg|webp|bmp|pdf|eps)$/i;
const CODE = /\.(py|r|m|jl|js|ts|lua|sh|sty|cls|bst|json|ya?ml|xml|cfg|def|latexmkrc|makefile)$/i;

function iconFor(path: string) {
  if (/\.bib$/i.test(path)) return <BookMarked size={14} />;
  if (IMAGE.test(path)) return <Image size={14} />;
  if (CODE.test(path)) return <CodeXml size={14} />;
  if (/\.(tex|txt|md|tikz|ltx)$/i.test(path)) return <FileText size={14} />;
  return <File size={14} />;
}

/** The folder to show beside a tab's name, when another tab has the same name. */
function folderHints(tabs: string[]): Map<string, string> {
  const byName = new Map<string, string[]>();
  for (const t of tabs) {
    const name = t.slice(t.lastIndexOf("/") + 1);
    byName.set(name, [...(byName.get(name) ?? []), t]);
  }
  const hints = new Map<string, string>();
  for (const group of byName.values()) {
    if (group.length < 2) continue;
    for (const t of group) hints.set(t, t.includes("/") ? t.slice(0, t.lastIndexOf("/")) : "/");
  }
  return hints;
}

/** The files you have open, as tabs above the editor. */
export function EditorTabs({
  tabs,
  active,
  tree,
  presence,
  onSelect,
  onClose,
  onReorder,
  children,
}: {
  tabs: string[];
  active: string | null;
  tree: TreeEntry[];
  /** Doc id -> colours of the other people in that file. */
  presence: Map<string, string[]>;
  onSelect: (path: string) => void;
  onClose: (paths: string[]) => void;
  onReorder: (tabs: string[]) => void;
  /** Shown at the right-hand end of the strip. */
  children?: ReactNode;
}) {
  const strip = useRef<HTMLDivElement>(null);
  const [menu, setMenu] = useState<{ x: number; y: number; path: string } | null>(null);
  const [dragging, setDragging] = useState<string | null>(null);
  const [dropAt, setDropAt] = useState<number | null>(null);
  const hints = folderHints(tabs);
  const docIds = new Map(tree.map((e) => [e.path, e.docId]));

  useEffect(() => {
    const el = strip.current?.querySelector<HTMLElement>(".etab.active");
    el?.scrollIntoView({ block: "nearest", inline: "nearest" });
  }, [active, tabs.length]);

  // A mouse wheel scrolls the strip sideways.
  useEffect(() => {
    const el = strip.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      if (Math.abs(e.deltaY) <= Math.abs(e.deltaX) || el.scrollWidth <= el.clientWidth) return;
      e.preventDefault();
      el.scrollLeft += e.deltaY;
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, []);

  const drop = () => {
    if (dragging !== null && dropAt !== null) {
      const without = tabs.filter((t) => t !== dragging);
      const at = dropAt > tabs.indexOf(dragging) ? dropAt - 1 : dropAt;
      without.splice(at, 0, dragging);
      if (without.join("\n") !== tabs.join("\n")) onReorder(without);
    }
    setDragging(null);
    setDropAt(null);
  };

  return (
    <div className="etabs">
      <div className="etabs-strip" ref={strip} role="tablist" aria-label="Open files" onDragOver={(e) => dragging && e.preventDefault()} onDrop={drop}>
        {tabs.map((path, i) => {
          const name = path.slice(path.lastIndexOf("/") + 1);
          const docId = docIds.get(path);
          const people = docId ? presence.get(docId) : undefined;
          return (
            <div
              key={path}
              role="tab"
              aria-selected={path === active}
              tabIndex={path === active ? 0 : -1}
              className={`etab${path === active ? " active" : ""}${dragging === path ? " dragging" : ""}${dropAt === i ? " drop-before" : ""}${
                dropAt === tabs.length && i === tabs.length - 1 ? " drop-after" : ""
              }`}
              title={path}
              draggable
              onClick={() => onSelect(path)}
              onAuxClick={(e) => {
                if (e.button === 1) {
                  e.preventDefault();
                  onClose([path]);
                }
              }}
              onMouseDown={(e) => e.button === 1 && e.preventDefault()}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") onSelect(path);
                else if (e.key === "Delete" || e.key === "Backspace") onClose([path]);
                else if (e.key === "ArrowRight" || e.key === "ArrowLeft") {
                  const next = tabs[i + (e.key === "ArrowRight" ? 1 : -1)];
                  if (next) {
                    onSelect(next);
                    requestAnimationFrame(() => strip.current?.querySelector<HTMLElement>(".etab.active")?.focus());
                  }
                }
              }}
              onContextMenu={(e) => {
                e.preventDefault();
                setMenu({ x: e.clientX, y: e.clientY, path });
              }}
              onDragStart={(e) => {
                e.dataTransfer.effectAllowed = "move";
                e.dataTransfer.setData("text/plain", path);
                setDragging(path);
              }}
              onDragOver={(e) => {
                if (!dragging) return;
                e.preventDefault();
                const r = e.currentTarget.getBoundingClientRect();
                setDropAt(e.clientX < r.left + r.width / 2 ? i : i + 1);
              }}
              onDragEnd={() => {
                setDragging(null);
                setDropAt(null);
              }}
            >
              <span className="etab-icon">{iconFor(path)}</span>
              <span className="etab-name">{name}</span>
              {hints.has(path) && <span className="etab-hint">{hints.get(path)}</span>}
              {people && (
                <span className="etab-people" title="Others are in this file">
                  {people.slice(0, 3).map((c, j) => (
                    <span key={j} style={{ background: c }} />
                  ))}
                </span>
              )}
              <button
                className="etab-close"
                title="Close"
                aria-label={`Close ${name}`}
                tabIndex={-1}
                onClick={(e) => {
                  e.stopPropagation();
                  onClose([path]);
                }}
              >
                <X size={13} />
              </button>
            </div>
          );
        })}
      </div>
      {children}
      {menu && (
        <ContextMenu
          x={menu.x}
          y={menu.y}
          onClose={() => setMenu(null)}
          items={[
            { label: "Close", onSelect: () => onClose([menu.path]) },
            { label: "Close others", disabled: tabs.length < 2, onSelect: () => onClose(tabs.filter((t) => t !== menu.path)) },
            {
              label: "Close tabs to the right",
              disabled: tabs.indexOf(menu.path) === tabs.length - 1,
              onSelect: () => onClose(tabs.slice(tabs.indexOf(menu.path) + 1)),
            },
            "-",
            { label: "Close all", onSelect: () => onClose(tabs) },
          ]}
        />
      )}
    </div>
  );
}
